import React, {useState, useEffect} from "react"
import {Box, Grid, Paper, Typography, CircularProgress, Chip} from "@mui/material"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import Autolink from "autolinker"
import styled from "styled-components"

import {NoteForm, Form, Field, FieldAnswered, Upload} from "./NoteForm"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"

const localTimeOptions = {
	year: "numeric",
	month: "short",
	day: "numeric",
	hour: "numeric",
	minute: "numeric",
	hour12: true,
	timeZoneName: "short",
} as const

function toLocalTime(dateString: string) {
	return new Intl.DateTimeFormat("default", localTimeOptions).format(new Date(dateString))
}

type Note = {
	ID: string
	CustomerID: string
	Form: {
		Type: string
		Version: number
		Fields: FieldAnswered[]
	}
	Uploads: Upload[]
	AgentName: string
	AgentEmail: string
	CreatedAt: string
}

type NotesResponse = {
	Forms: Form[]
	Notes: Note[]
}

const isTextareaField = (field: Field | FieldAnswered) => field.Type === "textarea"

function NoteItemCard({note}: {note: Note}) {
	const hasUploads = note.Uploads?.length > 0

	const handleDownloadUpload = (upload: Upload) => {
		window.open(api.customer.getNoteUploadContentURL(note.CustomerID, note.ID, upload.ID), "_blank")
	}

	const renderFieldContent = (field: FieldAnswered) => {
		if (isTextareaField(field)) {
			return (
				<NoteTypography
					variant="body2"
					dangerouslySetInnerHTML={{__html: Autolink.link(String(field.Content))}}
				/>
			)
		}
		return <Typography variant="body2">{field.Content}</Typography>
	}

	return (
		<Paper elevation={2}>
			<Box p={4}>
				<Grid direction="column" container spacing={2}>
					<Grid item container justifyContent="space-between" alignItems="center">
						<Grid item>
							<Box display="flex" alignItems="center">
								<Typography color="textSecondary" variant="subtitle2">
									<time>{toLocalTime(note.CreatedAt)}</time>
								</Typography>
								<Box ml={2}>
									<Chip label={note.Form.Type} size="small" variant="outlined" />
								</Box>
							</Box>
						</Grid>
						<Grid item>
							<Typography color="textSecondary" variant="subtitle2">
								{note.AgentName}
							</Typography>
						</Grid>
					</Grid>

					{note.Form.Fields.map((field, idx) => (
						<Grid item key={idx}>
							{note.Form.Fields.length > 1 && (
								<Typography variant="caption" color="textSecondary">
									{field.Name}:
								</Typography>
							)}
							{renderFieldContent(field)}
						</Grid>
					))}

					{hasUploads && (
						<Grid item>
							<Typography variant="caption" color="textSecondary">
								Attachments:
							</Typography>
							<Box mt={1}>
								{note.Uploads.map((upload) => (
									<Box key={upload.ID} display="inline-block" mr={1}>
										<Chip
											label={upload.FileName}
											size="small"
											icon={<AttachFileIcon />}
											onClick={() => handleDownloadUpload(upload)}
										/>
									</Box>
								))}
							</Box>
						</Grid>
					)}
				</Grid>
			</Box>
		</Paper>
	)
}

const NoteTypography = styled(Typography)`
	white-space: break-spaces;

	a {
		word-break: break-all;
	}
`

function NotesV2({
	customerID,
	reload,
	systemOfRecord,
}: {
	customerID: string
	reload: () => void
	systemOfRecord: string
}) {
	const {notification} = useNotification()
	const [loading, setLoading] = useState(true)
	const [notesData, setNotesData] = useState<NotesResponse | null>(null)

	const isLatitude = systemOfRecord === "Latitude"

	const loadNotes = () => {
		setLoading(true)
		api.customer
			.getNotes(customerID)
			.then((data) => {
				setNotesData(data)
			})
			.catch(() => {
				notification.error("Failed to load notes")
			})
			.finally(() => {
				setLoading(false)
			})
	}

	useEffect(() => {
		let isMounted = true

		const loadNotesWithGuard = () => {
			setLoading(true)
			api.customer
				.getNotes(customerID)
				.then((data) => {
					if (isMounted) {
						setNotesData(data)
					}
				})
				.catch(() => {
					if (isMounted) {
						notification.error("Failed to load notes")
					}
				})
				.finally(() => {
					if (isMounted) {
						setLoading(false)
					}
				})
		}

		loadNotesWithGuard()

		return () => {
			isMounted = false
		}
	}, [customerID, notification])

	const handleNoteAdded = () => {
		loadNotes()
		reload()
	}

	if (loading) {
		return (
			<Box p={3} display="flex" justifyContent="center">
				<CircularProgress />
			</Box>
		)
	}

	if (!notesData) {
		return (
			<Box p={3}>
				<Typography color="error">Failed to load notes</Typography>
			</Box>
		)
	}

	return (
		<Box p={3}>
			<NoteForm
				customerID={customerID}
				forms={notesData.Forms}
				disabled={isLatitude}
				onSuccess={handleNoteAdded}
			/>

			<Typography variant="subtitle1">{notesData.Notes.length === 0 ? "No notes" : "Notes"}</Typography>
			<Grid direction="column" spacing={2} container>
				{notesData.Notes.map((note, idx) => (
					<Grid key={idx} item>
						<NoteItemCard note={note} />
					</Grid>
				))}
			</Grid>
		</Box>
	)
}

export {NotesV2}
export type {Note, NotesResponse}
